import React, { useContext, useMemo, useState } from 'react';
import { useCardTitle } from '@/pages/dashboard/pages/monetization/components/useCardTitle';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import { Card, Col, Row } from 'tea-component';
import moment from 'moment';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import CardBox from '@/pages/dashboard/components/CardBox';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import { formatDataByChartType, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import CountryTabBreakdown, { renderTableCols } from '@/pages/dashboard/components/CountryRegionRowItem/CountryTabBreakdown';
import { renderValueCell } from '@/pages/dashboard/components/CountryRegionRowItem/CountryTabBreakdown/renderUtils';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import { resolveAndSelectName, resolveConfigString } from '@/pages/dashboard/utils/metricUtils';
import { useSelectorMap } from '@/pages/dashboard/utils/customHook';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import {
  formatExeclCommon,
  formatExeclSharePercent,
  formatNum,
  formatPercentNum,
  getChartDateFormatString,
  IsSingleDay,
  handleDownload
} from '@/pages/dashboard//utils/utils';
import './RevenueBreakdown.scss';

export default function RevenueBreakdown(props: any) {
  const { gameCode, metricsInfo } = useSelector((state: any) => ({
    gameCode: state.dashboard?.currentGame?.game_code,
    metricsInfo: state.dashboard?.metricsInfo,
  }), shallowEqual);
  const contextObj = useContext(FilterContext);
  const { filterVal: formValue } = contextObj || {};
  const { timeType = '', rangeTime = [] } = formValue || {};

  const {
    data: defaultData,
    defaultActive,
    state,
    isHaveBar,
    isHaveLine,
    isHavePie,
    metricsEnName,
    ratioEnName,
    metricsChName,
    ratioChName,
    showCountryDistribution = true,
    cardTitleMap,
    componentMetrics,
    dimensionColumn,
    tabMsg,
  } = props;
  const { regionDataType: segmentValue } = state;
  const { data: chartTableData, loading, errorMsg: error } = defaultData.MonetizationRankData || {};
  const { getSelectorText, getFilterItemText } = useSelectorMap();
  const { formatMessage, locale } = useIntl();
  const [iseye, setIseye] = useState(true);

  const DWM = {
    Day: formatMessage({ id: 'monetization_revenue_DAU' }),
    Week: formatMessage({ id: 'monetization_revenue_WAU' }),
    Month: formatMessage({ id: 'monetization_revenue_MAU' })
  };
  const SEGMENT_JSON = {
    country: formatMessage({ id: 'monetization_revenue_Country' }),
    region: formatMessage({ id: 'monetization_revenue_Region' }),
    zone: formatMessage({ id: 'monetization_revenue_Zone' })
  };

  const cardTitle = useCardTitle(segmentValue, cardTitleMap);

  const CHART_TITLE_JSON = {
    country: formatMessage({ id: 'monetization_revenue_Revenue_Country_Distribution' }),
    region: formatMessage({ id: 'monetization_revenue_Revenue_Region_Distribution' }),
    zone: formatMessage({ id: 'monetization_revenue_Revenue_Zone_Distribution' })
  };

  const isAvg = IsSingleDay({ ...formValue, period: PERIODTIMEOBJ?.[timeType] })
    ? ''
    : formatMessage({ id: 'monetization_revenue_Avg' });
  // columns data
  const isEN = getLocale() === 'en-US';

  const buildDynamicCols = () => {
    const metricCols = (componentMetrics || []).map((metric: Record<string, unknown>) => {
      const metricKey = metric.metricKey as string;
      const header = resolveAndSelectName(
        isEN,
        metric.enName as string,
        metric.cnName as string,
        metricsInfo,
      ) || metricKey;
      const formatRule = resolveConfigString(
        metric.formatRule as string,
        metricsInfo,
      );
      const dodFormatRule = metric.dodFormatRule as string || 'absPercent1Decimal';
      const width = isEN
        ? (metric.enWidth as number || 200)
        : (metric.cnWidth as number || 160);
      return {
        key: metricKey,
        header,
        width,
        align: 'center',
        render: (record: Record<string, unknown>) => {
          const metricData = (record?.[metricKey] || {}) as Record<string, string>;
          return renderValueCell({
            record: metricData,
            formatRule,
            changeFormatRule: dodFormatRule,
            isUserQuota: false,
          });
        },
      };
    });
    return [
      {
        key: 'name',
        header: resolveAndSelectName(
          isEN,
          dimensionColumn?.enName as string,
          dimensionColumn?.cnName as string,
          metricsInfo,
          { tabMsg: tabMsg?.[segmentValue] }
        ) || `${SEGMENT_JSON[segmentValue] || segmentValue}`,
        width: 200,
        align: 'left',
        dataType: segmentValue,
        fixed: 'left',
      },
      ...metricCols,
    ];
  };

  const colsConfig = componentMetrics
    ? buildDynamicCols()
    : [
      {
        key: 'name',
        header: `${SEGMENT_JSON[segmentValue]}`,
        width: 200,
        align: 'left',
        dataType: segmentValue,
        fixed: 'left'
      },
      //
      {
        key: 'revenue',
        header: `${isAvg ? formatMessage({ id: 'monetization_revenue_Sum' }) : ''}${formatMessage(
          { id: 'monetization_revenue_Revenue($)' },
          { unit: getMonetUnit() }
        )}`,
        width: 200,
        className: 'dbd-revenue-cols',
        align: 'center',
        dataType: 'twoDecimal',
        // unit:'$',
        isUserQuota: false,
        ratioField: 'revenue_dop',
        valueName: 'revenue'
      },
      {
        key: 'ave_dwm',
        header: `${isAvg}${DWM[timeType]}`,
        width: 180,
        align: 'center',
        dataType: 'integer'
      },
      {
        key: 'ave_arppu',
        header: `${isAvg}${formatMessage({ id: 'monetization_revenue_ARPPU($)' }, { unit: getMonetUnit() })}`,
        width: 180,
        align: 'center',
        dataType: 'twoDecimal'
      },
      {
        key: 'ave_arpu',
        header: `${isAvg}${formatMessage({ id: 'monetization_revenue_ARPU($)' }, { unit: getMonetUnit() })}`,
        width: 180,
        align: 'center',
        dataType: 'twoDecimal'
      },
      {
        key: 'ave_payer',
        header: `${isAvg}${formatMessage({ id: 'monetization_revenue_Payer%' }, { unit: getMonetUnit() })}`,
        width: 180,
        align: 'center',
        dataType: 'twoDecimalPercent'
      },
      {
        key: 'share_of_all',
        header: formatMessage({ id: 'monetization_revenue_Share_Of_All' }),
        width: 180,
        align: 'center',
        dataType: 'oneDecimalPercent'
      }
    ];
  // 筛选器选择多日期维度（多日、多周、多日）展示总Revenue与各指标的均值
  if (!componentMetrics && isAvg) {
    colsConfig.splice(2, 0, {
      key: 'ave_revenue',
      header: formatMessage({ id: 'monetization_revenue_Avg.Revenue($)' }, { unit: getMonetUnit() }),
      width: 200,
      align: 'center',
      dataType: 'twoDecimal'
    });
  }
  const chartTable = renderTableCols(colsConfig, segmentValue);

  const newChartTableData: any = useMemo(() => {
    const { dwm_data_list = [], dwm_list = [] } = chartTableData || {};
    // chart data
    const chartJson = {};
    // share data
    const shareJson = {};
    // 下载数据
    const downLoadDataArr: any[] = [];

    const chartArr = dwm_data_list?.[0]?.list?.map(({ crz_name }) => crz_name) || [];

    dwm_data_list?.forEach(({ date_ymd, list }) => {
      shareJson[date_ymd] = date_ymd ? { ...shareJson[date_ymd] } : {};
      list?.forEach(({ crz_name, full_crz_name, value, share }) => {
        chartJson[crz_name] = crz_name ? { ...chartJson[crz_name] } : {};

        !chartJson[crz_name].name && (chartJson[crz_name].name = getFilterItemText(segmentValue, crz_name, full_crz_name));
        // 柱状图堆积
        !chartJson[crz_name].stack && (chartJson[crz_name].stack = 'bar');
        chartJson[crz_name].stack = 'bar' && (chartJson[crz_name].barCategoryGap = '50%');
        chartJson[crz_name].data = chartJson[crz_name].data || [];
        chartJson[crz_name].data.push({
          x: date_ymd,
          y: value
        });

        // share data
        shareJson[date_ymd][chartJson[crz_name].name] = formatPercentNum(share, 1);

        downLoadDataArr.push({
          date_ymd: timeType === 'Month' ? moment(date_ymd).format('YYYY-MM') : moment(date_ymd).format('YYYY-MM-DD'),
          date: timeType === 'Month' ? moment(date_ymd).format('YYYY-MM') : moment(date_ymd).format('YYYY-MM-DD'),
          full_crz_name: getFilterItemText(segmentValue,crz_name, full_crz_name, true),
          value: formatExeclCommon(value),
          share: formatExeclSharePercent(share)
        });
      });
    });
    const filteredDownloadDataArr = downLoadDataArr.filter(
      record => record.full_crz_name !== 'Others' && record.full_crz_name !== '其他'
    );
    const newDwm_list = JSON.parse(JSON.stringify(dwm_list)).map((item: { name: string; full_name: string; }) => ({
      ...item,
      full_name: getFilterItemText(segmentValue, item?.name, item?.full_name),
      key: item.full_name
    }));

    // share all json
    const shareAllJson = {};
    newDwm_list?.forEach(({ key, share_of_all, full_name }) => {
      shareAllJson[key] = formatPercentNum(share_of_all, 1);
      shareAllJson[full_name] = formatPercentNum(share_of_all, 1);
    });

    const pieChartData: any[] = [];
    newDwm_list?.forEach(({ ave_revenue, key }) => {
      pieChartData.push({
        value: ave_revenue,
        name: getSelectorText(segmentValue, key)
      });
    });

    return {
      table: {
        option: chartTable,
        data: newDwm_list
      },
      chart: chartArr.map(item => chartJson[item]),
      shareJson,
      shareAllJson,
      pieChartData,
      downLoadData: {
        option: [
          {
            key: 'date',
            header: formatMessage({ id: 'monetization_revenue_Date' })
          },
          {
            key: 'full_crz_name',
            header: `${SEGMENT_JSON[segmentValue]}`
          },
          {
            key: 'value',
            header: formatMessage({ id: 'monetization_revenue_Revenue($)' }, { unit: getMonetUnit() })
          },
          {
            key: 'share',
            header: formatMessage({ id: 'monetization_revenue_Share' })
          }
        ],
        downloadData: filteredDownloadDataArr
      }
    };
  }, [chartTableData, getLocale()]);

  // 折线图、柱状图浮窗
  const formatterFuncLine = params => {
    let data: any[] = [];
    data = params?.map(({ seriesName, value, color, axisValue }) => {
      const name = seriesName ? seriesName : '';
      const metricsNum = value ? formatNum(value) : '-';
      return {
        name,
        metricsNum,
        ratioNum: newChartTableData?.shareJson?.[axisValue]?.[seriesName],
        color
      };
    });
    const isEn = locale === 'en-US';
    const metricsNameTitle = isEn ? metricsEnName : metricsChName;
    const ratioNameTitle = isEn ? ratioEnName : ratioChName;
    return getEchartTooltipText({
      title:
				timeType === 'Month' ? moment(params[0]?.axisValue).format('YYYY-MM') : moment(params[0]?.axisValue).format('YYYY-MM-DD'),
      metricsName: metricsNameTitle,
      ratioName: ratioNameTitle,
      hasRatio: true,
      data,
      isSort: true
    });
  };

  const config = {
    bar: {
      formatterFunc: formatterFuncLine,
      setOtherConfigFunc: (opt: { xAxis: { axisLabel: { formatter: (value: any) => any; }; }[]; }) => {
        opt.xAxis[0].axisLabel.formatter = (value: any) => getChartDateFormatString(timeType, value);
        return opt;
      }
    },
    line: {
      formatterFunc: formatterFuncLine,
      setOtherConfigFunc: (opt: { series: any[]; xAxis: { axisLabel: { formatter: (value: any) => any; }; }[]; }) => {
        opt.series = opt.series?.map((item: any) => ({
          ...item,
          // 折线图不堆积
          stack: ''
        }));
        opt.xAxis[0].axisLabel.formatter = value => getChartDateFormatString(timeType, value);
        // opt = addChartLabel(opt, {
        //   formatter: (params) => {
        //     const result = formatNum(params?.value, false)
        //     return result
        //   },
        //   show: iseye,
        //   formatNum: (value) => {
        //     return formatNum(value, false)
        //   }
        // })
        opt = xAxisHighLight(opt, {
          formatter: ({ value }) => timeType === 'Month' ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD')
        });
        return opt;
      }
    },
    pie: {
      formatterFunc: (params: { name: string; value: number; color: string; }) => {
        const { name, value, color } = params;
        const time = `${rangeTime[0]}${rangeTime[0] !== rangeTime[1] ? ` ~ ${  rangeTime[1]}` : ''}`;

        return getEchartTooltipText({
          title: time,
          metricsName: '',
          ratioName: '',
          hasRatio: true,
          data: [
            {
              name,
              metricsNum: value ? formatNum(value) : '-',
              ratioNum: newChartTableData?.shareAllJson[name],
              color
            }
          ]
        });
      },
      chartData: newChartTableData?.pieChartData
    }
  };

  const filteredNewChartTableData = newChartTableData?.chart?.filter(
    (record: { name: string; }) => record.name !== 'Others' && record.name !== '其他'
  );

  const lineChartConfig = isHaveBar ? formatDataByChartType('line', filteredNewChartTableData, config?.line) : [];
  const barChartConfig = isHaveLine ? formatDataByChartType('bar', filteredNewChartTableData, config?.bar) : [];
  const pieChartConfig = isHavePie ? formatDataByChartType('pie', filteredNewChartTableData, config?.pie) : [];

  const renderList = [
    // showEyesIcon && {
    //   type: 'eyes',
    //   svgType: iseye ? 'open-eyes' : 'eyes-closed',
    //   content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
    //   isHideCom: true,
    // },
    {
      type: 'barChart',
      // isHideCom: isHaveBar,
      isHideIcon: !isHaveBar,
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: (
        <BarChart
          height={320}
          title={''}
          legendData={barChartConfig?.legend || []}
          xAxisData={barChartConfig?.xAxisData || []}
          series={barChartConfig?.series || []}
          formatterFunc={(params: any) => config.bar.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config.bar.setOtherConfigFunc(opt)}
        />
      )
    },
    {
      type: 'lineChart',
      // isHideCom: isHaveLine,
      isHideIcon: !isHaveLine,
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: (
        <LineChart
          height={320}
          title={''}
          legendData={lineChartConfig?.legend || []}
          xAxisData={lineChartConfig?.xAxisData || []}
          series={lineChartConfig?.series || []}
          formatterFunc={(params: any) => config.line.formatterFunc(params)}
          setOtherConfigFunc={(opt: any) => config.line.setOtherConfigFunc(opt)}
        />
      )
    },
    {
      type: 'pieChart',
      // isHideCom: !isHavePie,
      isHideIcon: !isHavePie,
      svgType: 'pie-chart-icon',
      content: formatMessage({ id: 'common_pie_chart' }),
      component: (
        <PieChart
          height={320}
          isNodata={pieChartConfig?.chartData?.length === 0}
          chartData={pieChartConfig?.chartData || []}
          legendData={pieChartConfig?.legendData || []}
          formatterFunc={(params: any) => config.pie.formatterFunc(params)}
        />
      )
    }
  ];

  return (
    <>
      <Card className='card-sort'>
        <Card.Body title={<div className='card-head-breadcrumb '>{cardTitle}</div>}>
          {/* TODO:底部是否固定需要外部传入 */}
          <CountryTabBreakdown
            regionDataType={segmentValue}
            tableColumns={newChartTableData?.table?.option || []}
            loading={loading}
            isSingle={isAvg}
            isError={error}
            data={newChartTableData?.table?.data?.filter((record: { name: string; }) => record.name !== 'Others') || []}
            type={undefined}
            prefix={undefined}
            isHaveShare={undefined}
            isHaveDiscount={undefined}
            isUserQuota={undefined}
            unit={undefined}
            gameCode={gameCode}
            shareRename={undefined}
            isFooterFixed={false}
          />
        </Card.Body>
      </Card>
      {showCountryDistribution && (
        <Row gap={20} style={{ marginTop: 10 }}>
          <Col span={24} className='overview-section'>
            <CardBox
              className='register-table'
              height={320}
              bordered={false}
              titleCom={CHART_TITLE_JSON[segmentValue]}
              renderList={renderList}
              defaultAvtive={defaultActive}
              setCurrentDataType={(type: any) => {
                if (type === 'eyes') {
                  setIseye(!iseye);
                }
              // 柱状图、饼图不展示眼睛图标
              // setShowEyesIcon(type === 'lineChart' || type === 'eyes')
              }}
              downloadClick={async () => {
                handleDownload(newChartTableData?.downLoadData, CHART_TITLE_JSON[segmentValue]);
              }}
              isLoading={loading}
              isError={error}
              isNodata={newChartTableData?.table?.data?.length === 0}
            />
          </Col>
        </Row>
      )}
    </>
  );
}
